import React from 'react';
import { scrollToTop } from '../utils/scrollUtils';
import { Container, Typography, Button, Box, Grid, useTheme, Stack, alpha } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card } from '../design-system/components/Card';
import Footer from '../components/Footer';
import heroImageUrl from '../assets/images/cover-image.jpg'; // Import the local image

// Helper component for Material Symbols
const MaterialSymbol = ({ iconName, sx, weight = 300 }) => (
  <span
    className="material-symbols-outlined"
    style={{
      fontVariationSettings: `'FILL' 0, 'wght' ${weight}, 'GRAD' 0, 'opsz' 40`, // Control weight here
      fontSize: sx?.fontSize || 'inherit', // Apply fontSize from sx
      lineHeight: 1, // Ensure icon aligns well
      color: sx?.color, // Apply color from sx
      ...sx, // Apply other sx props
    }}
  >
    {iconName}
  </span>
);

function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark'; // Helper variable

  const features = [
    {
      icon: <MaterialSymbol iconName="auto_stories" sx={{ fontSize: 40 }} />,
      title: "Interactive Modules",
      description: "Engage with dynamic flashcards and quizzes that adapt to your learning pace, making complex concepts easier to understand and remember."
    },
    {
      icon: <MaterialSymbol iconName="psychology" sx={{ fontSize: 40 }} />,
      title: "Real Case Studies",
      description: "Learn from real-world scenarios that simulate actual caregiving situations, helping you develop critical thinking and decision-making skills."
    },
    {
      icon: <MaterialSymbol iconName="emoji_objects" sx={{ fontSize: 40 }} />,
      title: "Expert-Led Content",
      description: "Access curriculum designed by healthcare professionals with years of experience in caregiving and patient safety."
    },
    {
      icon: <MaterialSymbol iconName="groups" sx={{ fontSize: 40 }} />,
      title: "Community Support",
      description: "Join a community of caregivers, share experiences, and learn from others while building your professional network."
    }
  ];

  // --- Animation Variants --- 
  const basicFadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5 } }
  };

  const fadeAndSlideUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15 // Adjust stagger timing as needed
      }
    }
  };
  
  const heroCardContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1, // Faster stagger for small cards
        delayChildren: 0.3 // Delay start after hero text
      }
    }
  }

  // Add a variant for the new abstract element
  const abstractElementVariant = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { 
      opacity: 1, 
      scale: 1, 
      transition: { duration: 0.8, delay: 0.4, ease: "easeOut" }
    }
  }
  // --- End Animation Variants --- 

  return (
    <Box sx={{ 
      width: '100%', 
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      position: 'relative',
      backgroundColor: theme.palette.mode === 'light' ? 'grey.100' : 'grey.900', // Match footer background
    }}>
      <Box sx={{ 
        flex: '1 0 auto',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default, // Match main content background
      }}> 
        {/* Hero Section - Full width (12 columns) */}
        <Grid container spacing={0} sx={{ minHeight: { xs: '100vh', md: '80vh' }, position: 'relative' }}>
          <Grid item xs={12}>
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(${heroImageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'brightness(0.7)',
                  zIndex: 0,
                },
              }}
            >
              <Container maxWidth="lg" sx={{ 
                height: '100%', 
                position: 'relative', 
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                pt: { xs: '15vh', md: '10vh' },
              }}>
                <Grid 
                  container 
                  spacing={4} 
                  sx={{ 
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    pt: { xs: 0, md: 0 },
                    mb: { xs: 2, md: 3 }
                  }}
                >
                  <Grid item xs={12} md={6}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                    >
                      <Typography
                        variant="h1"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          mb: 2,
                          fontSize: { xs: '2.5rem', md: '3.5rem' },
                        }}
                      >
                        Grow as a
                        <br />
                        Compassionate
                        <br />
                        Caregiver
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          color: 'white',
                          mb: 4,
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                        }}
                      >
                        Learn vital caregiving techniques with expert-led lessons, real-world scenarios, and a community that supports your journey every step of the way.
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<MaterialSymbol iconName="school" sx={{ fontSize: '1.2em' }} />}
                        onClick={() => {
                          navigate('/courses');
                          scrollToTop();
                        }}
                        sx={{
                          py: 1.5,
                          px: 5,
                          borderRadius: '50px',
                          textTransform: 'none',
                          fontSize: { xs: '1rem', md: '1.1rem' },
                          fontWeight: 600,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                          },
                        }}
                      >
                        Start Learning Today
                      </Button>
                    </motion.div>
                  </Grid>
                </Grid>

                {/* Feature Icons - Center Aligned */}
                <Box sx={{ 
                  width: '100%', 
                  mt: { xs: '15vh', md: 'auto' },
                  mb: { xs: 4, md: 10 },
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <motion.div 
                    variants={heroCardContainer}
                    initial="hidden"
                    animate="visible"
                    style={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%'
                    }}
                  >
                    <Grid 
                      container 
                      spacing={2} 
                      sx={{ 
                        maxWidth: { xs: '300px', sm: '800px' },
                        margin: 0 // Reset margin
                      }}
                    >
                      {features.map((feature, index) => (
                        <Grid 
                          item 
                          xs={6} 
                          sm={3} 
                          key={feature.title}
                          sx={{
                            display: 'flex',
                            height: { xs: '100px', sm: '120px' }, // Fixed height for consistency
                          }}
                        >
                          <motion.div 
                            variants={fadeAndSlideUp}
                            style={{
                              width: '100%',
                              height: '100%'
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%',
                              width: '100%',
                              textAlign: 'center',
                              backgroundColor: 'rgba(0, 0, 0, 0.25)',
                              backdropFilter: 'blur(6px)',
                              borderRadius: '16px',
                              p: { xs: 1.5, sm: 2 },
                              border: '0.5px solid rgba(255, 255, 255, 0.3)',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                transform: 'translateY(-5px)',
                                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
                              }
                            }}>
                              <Box sx={{ 
                                color: 'rgba(255, 255, 255, 1)',
                                mb: { xs: 1, sm: 1.5 },
                                display: 'inline-flex'
                              }}>
                                <MaterialSymbol 
                                  iconName={feature.icon.props.iconName} 
                                  sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' } }}
                                />
                              </Box>
                              <Typography 
                                variant="body2"
                                fontWeight={600}
                                color="rgba(255, 255, 255, 1)"
                                align="center"
                                sx={{ 
                                  fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                  lineHeight: 1.3,
                                  px: 1
                                }}
                              >
                                {feature.title}
                              </Typography>
                            </Box>
                          </motion.div>
                        </Grid>
                      ))}
                    </Grid>
                  </motion.div>
                </Box>
              </Container>
            </Box>
          </Grid>
        </Grid>

        {/* Features Section - 2x2 Grid */}
        <Container sx={{ py: { xs: 6, sm: 8, md: 10 } }}>
          <motion.div
             variants={fadeAndSlideUp}
             initial="hidden"
             whileInView="visible"
             viewport={{ once: true, amount: 0.3 }}
          >
            <Typography 
              variant="h3" 
              component="h2" 
              align="center"
              color="text.primary" 
              sx={{ 
                mb: { xs: 5, sm: 6, md: 8 },
                fontWeight: 700,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
              }}
            >
              Elevate Your Caregiving Skills
            </Typography>
          </motion.div>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <Grid container spacing={{ xs: 3, sm: 4, md: 5 }}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <motion.div style={{ height: '100%' }} variants={fadeAndSlideUp}> 
                    <Card variant="outlined" sx={{
                      p: { xs: 2.5, sm: 3, md: 4 },
                      height: '100%',
                      borderColor: 'divider',
                      borderRadius: '20px',
                      transition: 'all 0.3s ease',
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 8px 25px ${theme.palette.primary.main}18`,
                        borderColor: 'primary.light',
                      },
                    }} >
                      <Box sx={{ mb: 2.5, color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main, lineHeight: 1 }}>
                        <MaterialSymbol 
                         iconName={feature.icon.props.iconName} 
                         sx={{ fontSize: { xs: '2rem', md: '2.2rem' } }}
                        />
                      </Box>
                      <Typography 
                        variant="h5" 
                        component="h3" 
                        color="text.primary"
                        sx={{ 
                          mb: 1.5,
                          fontWeight: 600,
                          fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.4rem' },
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                          lineHeight: 1.6,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>

        {/* Call to Action Section */}
        <Box 
          sx={{ 
            bgcolor: theme.palette.mode === 'light' ? 'primary.dark' : 'primary.main',
            color: 'white',
            py: { xs: 6, sm: 8, md: 10 },
            mt: { xs: 6, sm: 8, md: 10 }
          }}
        >
          <Container>
            <Box sx={{ textAlign: 'center', maxWidth: { xs: '100%', md: 800 }, mx: 'auto' }}>
               <motion.div
                 variants={staggerContainer}
                 initial="hidden"
                 whileInView="visible"
                 viewport={{ once: true, amount: 0.3 }}
               >
                 <motion.div variants={fadeAndSlideUp}>
                   <Typography variant="h3" component="h2" sx={{ mb: { xs: 2, md: 3 }, fontWeight: 700, fontSize: { xs: '1.6rem', sm: '2rem', md: '2.5rem' } }}>
                     Ready to Advance Your Career?
                   </Typography>
                 </motion.div>
                 <motion.div variants={fadeAndSlideUp}>
                   <Typography variant="h6" sx={{ mb: { xs: 4, md: 5 }, opacity: 0.9, fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>
                     Join thousands of caregivers who have enhanced their skills and transformed their careers through our platform
                   </Typography>
                 </motion.div>
                 <motion.div variants={fadeAndSlideUp}>
                   <Button
                     variant="contained"
                     size="large"
                     onClick={() => {
                         navigate('/courses');
                         scrollToTop();
                       }}
                     sx={{ 
                       bgcolor: 'white',
                       color: theme.palette.mode === 'light' ? 'primary.dark' : 'primary.main',
                       py: { xs: 1.2, sm: 1.5 },
                       px: { xs: 4, sm: 6 },
                       borderRadius: '50px',
                       textTransform: 'none',
                       fontSize: { xs: '1rem', md: '1.1rem' },
                       fontWeight: 600,
                       boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                       transition: 'all 0.3s ease',
                       '&:hover': {
                         bgcolor: 'grey.200',
                         transform: 'translateY(-3px)',
                         boxShadow: '0 6px 15px rgba(0,0,0,0.3)',
                       },
                     }}
                   >
                     Get Started Today
                   </Button>
                 </motion.div>
               </motion.div>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ 
        flexShrink: 0,
        marginTop: 'auto', // Push to bottom
        display: 'flex',
        width: '100%'
      }}>
        <Footer />
      </Box>
    </Box>
  );
}

export default Home;
